import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";

import UserAvatar from "../../../../components/avatar/UserAvatar";
import {
  useGetShotCommentsQuery,
  type ShotComment,
} from "../../../../app/services/shotComments";
import { isClientGroupPosition } from "../../../../utils/memberPositionUtils";
import { Shot } from "../../../../app/services/shots";
import {
  splitTimecodeSegments,
  type TimecodeSegment,
} from "../../../../utils/timecode";

import {
  layoutCommentBubbles,
  CLOUD_STAGGER_SEC,
  CLOUD_BUBBLE_FADE_SEC,
  type CloudBubbleSpec,
  type CloudRect,
} from "../../../../utils/cloud-layout/commentCloudLayout";

/**
 * 選択中（ホイール中央）ショットの「相手側の未解決コメント」を、カード周辺に
 * 雲のように散らして表示するオーバーレイ。
 *
 *  - 相手側 = 自分がクライアントグループ（CLIENT/REVIEWER）なら制作側
 *    （PRODUCER/DIRECTOR/ADMIN/SPECIALIST）のコメント、逆も同様
 *    （メール通知の相手側判定と同じ軸）。
 *  - 古い順に上から下へ並べつつ、横はカードの左右バンドへ交互に散らす
 *    （バンド内位置はコメント id のハッシュで決定的。再レンダリングで踊らない）。
 *    カードサムネイルへの重なりは許容。
 *  - 表示は最新 MAX_BUBBLES 件まで。あふれた分は「他 +N件」チップに集約。
 *  - タイムコードピン付きコメントはピン時刻のサムネイルを本文と横並びで
 *    バブルの端にぴったり添える（本文と同程度の面積・バブルごとに左右交互）。
 *    本文中の [M:SS] タグはテキスト表示せず、その位置にタイムコードチップ UI
 *    を埋め込む（Preview の再生チップと同じ重複回避）。
 *    タグが無いレガシーコメント（pin_time のみ）はフィールド値をバッジ表示する。
 *  - カードが中央にナッジ（settled）した後、1 件ずつスタガーでフェードインする。
 *    全件の表示が終わったら onRevealComplete で親へ通知する
 *    （アクションパネルの遅延フェードインの起点）。
 *
 * レイヤは pointer-events: none の viewport 固定オーバーレイ（body ポータル）
 * なので、ホイールのジェスチャ（Stage 上の wheel / touch）を一切妨げない。
 */

/** 同時に表示するバブルの上限。あふれは「他 +N件」チップへ。 */
const MAX_BUBBLES = 6;
/** ピンフレームサムネイルの幅(px)。本文と同程度の面積になる大きさ。 */
const PIN_SQUARE_PX = 88;

// ===== Styled =====

const CloudLayer = styled.div`
  position: fixed;
  inset: 0;
  /* Stage(z=2) より上、パネル(z=3) と同層。操作は一切奪わない。 */
  z-index: 3;
  pointer-events: none;
`;

const Bubble = styled(motion.div)`
  position: absolute;
  border-radius: 12px;
  border: 1px solid var(--soremo-border);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  /* ピンフレームをバブルの端（角丸）にぴったり沿わせるためクリップする */
  overflow: hidden;
`;

/**
 * サムネイルと本文の横並び行。サムネイルはバブルの左右どちらかの端に
 * ぴったり揃え、バブルごとに左右交互へ切り替える（$imageRight）。
 */
const BubbleRow = styled.div<{ $imageRight: boolean }>`
  display: flex;
  flex-direction: ${({ $imageRight }) => ($imageRight ? "row-reverse" : "row")};
  align-items: stretch;
`;

/** 本文エリア（サムネイルの反対側）。 */
const BubbleBody = styled.div`
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BubbleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 20px;
`;

const BubbleAuthor = styled.span.attrs({
  className: "bodytext-11 u-text-light-gray",
})`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BubbleText = styled.p.attrs({ className: "bodytext-13" })`
  margin: 0;
  line-height: 1.5;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  word-break: break-word;
`;

/**
 * ピン位置のフレームサムネイル。バブルの端に上下いっぱいでぴったり付ける
 * （角丸のクリップはバブル側の overflow: hidden が担う）。
 */
const PinFrame = styled.div`
  position: relative;
  flex-shrink: 0;
  align-self: stretch;
  width: ${PIN_SQUARE_PX}px;
  /* 本文が短くても最低ほぼ正方形を保つ（バブル全体もこの高さまで伸びる） */
  min-height: ${PIN_SQUARE_PX}px;
  background: var(--soremo-background);
`;

const PinFrameImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/** フレーム画像右下のタイムコードバッジ。 */
const PinFrameTimecode = styled.span.attrs({ className: "bodytext-11" })`
  position: absolute;
  right: 4px;
  bottom: 4px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 5px;
  border-radius: 999px;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);

  .material-symbols-rounded {
    font-size: 11px;
  }
`;

/** ピン画像が無いタイムコードのみのコメント用の行。 */
const PinTimecode = styled.span.attrs({
  className: "bodytext-11 u-text-light-gray",
})`
  display: inline-flex;
  align-items: center;
  gap: 2px;

  .material-symbols-rounded {
    font-size: 14px;
  }
`;

/**
 * 本文中のタイムコードタグ位置に埋め込むインラインチップ。
 * Preview の再生チップと同じトーン（テキストのタグ表示は行わない）。
 */
const InlineTimecodeChip = styled.span.attrs({ className: "bodytext-11" })`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 6px;
  margin: 0 2px;
  border-radius: 999px;
  background: var(--soremo-background);
  color: var(--soremo-light-gray);
  white-space: nowrap;
  vertical-align: baseline;

  &::before {
    content: "\u25B6";
    font-size: 8px;
  }
`;

const OverflowChip = styled(motion.div).attrs({
  className: "bodytext-11 u-text-light-gray",
})`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid var(--soremo-border);
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

// ===== サイズ見積もり =====

const clampNumber = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

/** タイムコードチップの全角文字換算幅（インライン埋め込みの見積もり用）。 */
const CHIP_CHAR_EQUIV = 5;

/** チップ表示用ラベル（絶対フレーム "300F" は Preview と同じ数値表示にする）。 */
const timecodeChipLabel = (value: string): string =>
  /^\d+F$/.test(value) ? value.slice(0, -1) : value;

/** 本文の分解結果から表示上の実効文字数を数える（タグはチップ幅換算）。 */
const effectiveTextLength = (segments: TimecodeSegment[]): number =>
  segments.reduce(
    (total, seg) =>
      total + (seg.type === "text" ? seg.value.length : CHIP_CHAR_EQUIV),
    0,
  );

/**
 * バブルの描画サイズを本文長から見積もる（レイアウト計算用）。
 * 実描画は width を固定し、本文は 3 行でクランプするため、
 * この見積もりと大きくズレない。
 */
const estimateBubbleSize = (
  comment: ShotComment,
): { width: number; height: number } => {
  const segments = splitTimecodeSegments(comment.comment || "");
  const length = effectiveTextLength(segments);
  const hasPinImage = Boolean(comment.pin_thumbnail_url);
  const bodyHasTag = segments.some((seg) => seg.type === "timecode");
  const hasLegacyTimecodeRow =
    !hasPinImage && !bodyHasTag && Boolean(comment.timecode_start);

  if (!hasPinImage) {
    // 全角 13px 想定。短文は小さく、長文は 280px で頭打ち。
    const width = Math.round(clampNumber(24 + length * 13, 200, 280));
    const charsPerLine = Math.max(1, Math.floor((width - 24) / 13));
    const lines = clampNumber(Math.ceil(length / charsPerLine), 1, 3);
    // padding(20) + ヘッダ(20+6) + 本文 + レガシータイムコード行
    const height = 20 + 26 + lines * 20 + (hasLegacyTimecodeRow ? 26 : 0);
    return { width, height };
  }

  // 左右レイアウト: 端にぴったり付くサムネイル + 本文（面積が同程度になる幅配分）
  const width = Math.round(
    clampNumber(24 + PIN_SQUARE_PX + length * 13, 248, 300),
  );
  const textWidth = width - PIN_SQUARE_PX - 24;
  const charsPerLine = Math.max(1, Math.floor(textWidth / 13));
  const lines = clampNumber(Math.ceil(length / charsPerLine), 1, 3);
  // 画像はバブルの上下いっぱいに広がるため、高さは本文と一辺の大きい方
  const height = Math.max(PIN_SQUARE_PX, 20 + 26 + lines * 20);
  return { width, height };
};

/** 「他 +N件」チップの固定サイズ。 */
const OVERFLOW_CHIP_SIZE = { width: 88, height: 30 };
const OVERFLOW_CHIP_ID = "__overflow__";

// ===== 本体 =====

export interface ShotCommentCloudProps {
  shot: Shot;
  /** 自分のプロジェクト内ポジション（相手側判定の基準）。 */
  projectUserPosition: string;
  /** 中央カードの viewport 矩形。 */
  cardRect: CloudRect;
  /** バブルを置いてよい viewport 領域（クローム / パネルを除いた範囲）。 */
  bounds: CloudRect;
  /** カードが中央にナッジ（settled）しているか。フェードインの起点。 */
  visible: boolean;
  /**
   * 全バブルのフェードイン完了時（コメントが 1 件も無い場合は確定した時点）に
   * 一度呼ばれる。visible が false に戻ると再度スケジュールし直す。
   */
  onRevealComplete?: () => void;
}

const ShotCommentCloud: React.FC<ShotCommentCloudProps> = ({
  shot,
  projectUserPosition,
  cardRect,
  bounds,
  visible,
  onRevealComplete,
}) => {
  const {
    data: commentsData,
    isSuccess,
    isError,
  } = useGetShotCommentsQuery(
    { shot_id: shot.id, filter: "unresolved", limit: 100 },
    { skip: !shot.id },
  );

  // 相手側（自分と反対のグループ）の未解決コメントを古い順で並べる。
  const oppositeComments = useMemo(() => {
    const all = commentsData?.data?.comments ?? [];
    const mySideIsClient = isClientGroupPosition(projectUserPosition);
    return all
      .filter((c) => !c.parent_id)
      .filter((c) => {
        const authorPosition = c.author?.position;
        if (!authorPosition) return false;
        return isClientGroupPosition(authorPosition) !== mySideIsClient;
      })
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
  }, [commentsData, projectUserPosition]);

  // 表示は最新 MAX_BUBBLES 件（上から古い順を維持）。残りはチップに集約。
  const visibleComments = useMemo(
    () => oppositeComments.slice(-MAX_BUBBLES),
    [oppositeComments],
  );
  const overflowCount = oppositeComments.length - visibleComments.length;

  // ピン画像付きバブルだけを数え、画像サイドを左右交互に割り当てる。
  const pinImageSides = useMemo(() => {
    const sides = new Map<string, "left" | "right">();
    let count = 0;
    visibleComments.forEach((c) => {
      if (c.pin_thumbnail_url) {
        sides.set(c.id, count % 2 === 0 ? "left" : "right");
        count += 1;
      }
    });
    return sides;
  }, [visibleComments]);

  const placements = useMemo(() => {
    if (visibleComments.length === 0) return [];
    const specs: CloudBubbleSpec[] = visibleComments.map((c) => ({
      id: c.id,
      ...estimateBubbleSize(c),
    }));
    if (overflowCount > 0) {
      specs.push({ id: OVERFLOW_CHIP_ID, ...OVERFLOW_CHIP_SIZE });
    }
    return layoutCommentBubbles(specs, cardRect, bounds);
  }, [visibleComments, overflowCount, cardRect, bounds]);

  const placementById = useMemo(
    () => new Map(placements.map((p) => [p.id, p])),
    [placements],
  );

  // 全バブルのフェードイン完了を親へ通知する（クエリ確定前は通知しない）。
  const bubbleCount = visibleComments.length + (overflowCount > 0 ? 1 : 0);
  useEffect(() => {
    if (!visible || !onRevealComplete) return undefined;
    if (!isSuccess && !isError) return undefined;
    if (bubbleCount === 0) {
      onRevealComplete();
      return undefined;
    }
    const totalMs =
      ((bubbleCount - 1) * CLOUD_STAGGER_SEC + CLOUD_BUBBLE_FADE_SEC) * 1000;
    const timer = window.setTimeout(() => onRevealComplete(), totalMs);
    return () => window.clearTimeout(timer);
  }, [visible, isSuccess, isError, bubbleCount, onRevealComplete]);

  if (!visible || visibleComments.length === 0) {
    // AnimatePresence の exit を効かせるため、レイヤ自体は残して空にする。
    return createPortal(
      <CloudLayer aria-hidden="true">
        <AnimatePresence />
      </CloudLayer>,
      document.body,
    );
  }

  return createPortal(
    <CloudLayer>
      <AnimatePresence>
        {visibleComments.map((comment, i) => {
          const placement = placementById.get(comment.id);
          if (!placement) return null;
          const hasPinImage = Boolean(comment.pin_thumbnail_url);
          const segments = splitTimecodeSegments(comment.comment || "");
          const bodyHasTag = segments.some((seg) => seg.type === "timecode");
          // 本文タグが無いレガシーコメントのみフィールド値をバッジ表示する
          const legacyTimecode = bodyHasTag ? null : comment.timecode_start;
          return (
            <Bubble
              key={comment.id}
              style={{
                left: placement.left,
                top: placement.top,
                width: placement.width,
              }}
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                  delay: i * CLOUD_STAGGER_SEC,
                  duration: CLOUD_BUBBLE_FADE_SEC,
                  ease: "easeOut",
                },
              }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
            >
              <BubbleRow $imageRight={pinImageSides.get(comment.id) === "right"}>
                {hasPinImage && (
                  <PinFrame>
                    <PinFrameImage
                      src={comment.pin_thumbnail_url as string}
                      alt=""
                      loading="lazy"
                    />
                    {legacyTimecode && (
                      <PinFrameTimecode>
                        <span
                          className="material-symbols-rounded"
                          aria-hidden="true"
                        >
                          schedule
                        </span>
                        {legacyTimecode}
                      </PinFrameTimecode>
                    )}
                  </PinFrame>
                )}
                <BubbleBody>
                  <BubbleHeader>
                    <UserAvatar
                      userId={comment.author?.id}
                      avatar={comment.author?.avatar}
                      displayName={comment.author?.display_name}
                      size={20}
                    />
                    <BubbleAuthor>{comment.author?.display_name}</BubbleAuthor>
                  </BubbleHeader>
                  <BubbleText>
                    {segments.map((seg, segIndex) =>
                      seg.type === "timecode" ? (
                        // eslint-disable-next-line react/no-array-index-key
                        <InlineTimecodeChip key={segIndex}>
                          {timecodeChipLabel(seg.value)}
                        </InlineTimecodeChip>
                      ) : (
                        // eslint-disable-next-line react/no-array-index-key
                        <React.Fragment key={segIndex}>
                          {seg.value}
                        </React.Fragment>
                      ),
                    )}
                  </BubbleText>
                  {!hasPinImage && legacyTimecode && (
                    <PinTimecode>
                      <span
                        className="material-symbols-rounded"
                        aria-hidden="true"
                      >
                        schedule
                      </span>
                      {legacyTimecode}
                    </PinTimecode>
                  )}
                </BubbleBody>
              </BubbleRow>
            </Bubble>
          );
        })}
        {overflowCount > 0 &&
          (() => {
            const placement = placementById.get(OVERFLOW_CHIP_ID);
            if (!placement) return null;
            return (
              <OverflowChip
                key={OVERFLOW_CHIP_ID}
                style={{
                  left: placement.left,
                  top: placement.top,
                  width: placement.width,
                  height: placement.height,
                }}
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: visibleComments.length * CLOUD_STAGGER_SEC,
                    duration: CLOUD_BUBBLE_FADE_SEC,
                    ease: "easeOut",
                  },
                }}
                exit={{ opacity: 0, transition: { duration: 0.15 } }}
              >
                他 +{overflowCount}件
              </OverflowChip>
            );
          })()}
      </AnimatePresence>
    </CloudLayer>,
    document.body,
  );
};

export default ShotCommentCloud;
